import { EventType, AnalyticsUser, AnalyticsEvent } from '../api/models/analytics'
import { log, logGroupCollapsed, logGroupEnd } from './debug'
import {
  localStorageGet,
  localStorageSet,
  trackHighlightEvent,
  trackPostHogEvent,
  identifyPostHogUser,
  generateId,
  isBuilderEditingMode,
} from './utils'
import startSessionReplay from './sessionReplay'

const searchParams = new URL(window.location.toString()).searchParams
export const TRACK_ANALYTICS =
  (!window.location.host.startsWith('localhost:') &&
    !isBuilderEditingMode() &&
    searchParams.get('savvy_track') !== 'false') ||
  searchParams.get('savvy_track') === 'true'
const ongoingTrackData: Record<string, any> = {}
const ongoingTrackDataForFlow: Record<string, Record<string, any>> = {}
const pageloadId = getPageloadId()

let userId: string
let setupTriggered = false
let sessionReplayStarted = false

declare global {
  interface Window {
    jitsu: Function
    jitsuQ: unknown[]
    jitsuNext: any
    jitsuNextQ: unknown[]
    location: Location
    jitsuLoaded?: Function
  }
}

const PREV_CLOUD_ENTRY_DATA: Record<string, Record<string, any>> = {}

export default {
  setup,
  track,
  identify,
  set,
  setForFlow,
  startRecordingSessionReplay,
}

export async function setup(groupId: string, projectId: string) {
  if (!TRACK_ANALYTICS || setupTriggered) return

  if (allowJitsuNext()) setUpJitsuNext()
  if (allowJitsu1_0()) setUpJitsu1_0()

  setupTriggered = true

  userId = getGlobalUserId(projectId)
  const legacy_product_id = getLegacyProductId()

  set({
    group_id: groupId,
    project_id: projectId,
    user_id: userId,
    pageload_id: pageloadId,
    legacy_product_id,
  })
  identify({ id: userId })
  track('webpage:viewed', { view_type: 'load' })

  window.history.pushState = new Proxy(window.history.pushState, {
    apply: (target, thisArg, argArray) => {
      try {
        // Not just an anchor link change
        const urlPath = argArray[2] instanceof URL ? argArray[2].href : argArray[2]
        if (!urlPath.startsWith('#')) track('webpage:viewed', { view_type: 'view' })
        return target.apply(thisArg, argArray as any)
      } catch (err) {
        console.warn('Failed to track history.pushState in Embeddables Analytics', err)
        return target.apply(thisArg, argArray as any)
      }
    },
  })
  // @TODO: make this work if the element is added dynamically after this point
}

function setUpJitsu1_0() {
  window.jitsuNextQ = []
  window.jitsu =
    window.jitsu ||
    function () {
      ;(window.jitsuQ = window.jitsuQ || []).push(arguments)
    }

  var script = document.createElement('script')
  script.type = 'text/javascript'
  script.src = 'https://t.jitsu.com/s/lib.js'
  script.setAttribute('data-init-only', 'false')
  script.setAttribute('defer', 'true')
  script.setAttribute('data-key', 'js.w0ovvxugd8gfml4xqqaclc.0pzubyvfcjqs2rktl1s2yzt')
  document.head.appendChild(script)
}

// const a: AnalyticsActionTriggeredEvent = {
//   env: 'prod',
//   flow_id: '',
//   flow_version: '',
//   is_live_flow_version: '',
//   flow_page_id: '',
//   flow_page_key: '',
//   flow_page_index: '',
//   max_page_id: '',
//   max_page_key: '',
//   max_page_index: '',
//   timestamp: new Date(),
//   group_id: 'string',
//   user_id: 'string',
//   event_type: 'action:triggered',
//   doc_host: 'string',
//   doc_url: 'string',
//   doc_path: 'string',
//   doc_title: 'string',
//   doc_referrer: 'string',
//   doc_search: 'string',
//   doc_hash: 'string',
//   doc_width: 0,
//   doc_height: 0,
//   device: {},
//   utm_source: 'string',
//   utm_medium: 'string',
//   utm_campaign: 'string',
//   utm_term: 'string',
//   utm_content: 'string',
//   gclid: 'string',
//   fbclid: 'string',
//   action_type: 'code',
//   action_id: '',
//   action_name: '',
//   trigger_type: 'page:viewed',
//   trigger_path: '',
// }

export function startRecordingSessionReplay(projectId: string) {
  if (!TRACK_ANALYTICS) return

  if (!sessionReplayStarted) startSessionReplay(projectId, userId, pageloadId)
  sessionReplayStarted = true
}
export function track(
  event_type: EventType,
  data?: Partial<AnalyticsEvent>,
  ...args: any[]
): Promise<void> {
  try {
    trackHighlightEvent(event_type, { ...ongoingTrackData, ...data })
    trackPostHogEvent(event_type, { ...ongoingTrackData, ...data })
  } catch (error) {}
  return runJitsu('track', event_type, data, ...args)
}
export function identify(data: Partial<AnalyticsUser>, ...args: any[]) {
  try {
    identifyPostHogUser({ ...ongoingTrackData, ...data })
    // sendToTrackingQueue({ ...ongoingTrackData, ...data })
  } catch (error) {}
  return runJitsu('id', data, ...args)
}
export function set(data: Partial<AnalyticsEvent>, ...args: any[]) {
  // @TODO: Use ongoingTrackData to set data for track() calls in Jitsu iself
  // Because Jitsu doesn't handle multiple flows on the page with different flow_ids, userDatas etc.
  try {
    Object.entries(data || {}).forEach(([key, value]) => {
      ongoingTrackData[key] = value
    })
  } catch (error) {
    console.warn('Error setting Jitsu data for track() calls in other services:', error)
  }

  return runJitsu('set', data, ...args)
}
export function setForFlow(flowId: string, data: Partial<AnalyticsEvent>, ...args: any[]) {
  // @TODO: Use ongoingTrackData to set data for track() calls in Jitsu iself
  // Because Jitsu doesn't handle multiple flows on the page with different flow_ids, userDatas etc.
  try {
    if (!ongoingTrackDataForFlow[flowId]) ongoingTrackDataForFlow[flowId] = {}
    Object.entries(data || {}).forEach(([key, value]) => {
      ongoingTrackDataForFlow[flowId][key] = value
    })
  } catch (error) {
    console.warn('Error setting Jitsu data for track() calls in other services:', error)
  }
}
function runJitsu(method: string, ...args: any[]): Promise<void> {
  try {
    if (searchParams.get('savvy_debug') === 'true' || localStorageGet('savvy_debug')) {
      logGroupCollapsed(`Jitsu ${method.toUpperCase()}`)
      log(...args)
      logGroupEnd()
    }
    if (!TRACK_ANALYTICS) return Promise.resolve()

    // if (tooLarge(args[0], 25)) {
    //   console.log(
    //     `Jitsu ${method.toUpperCase()} skipping old method - Too Large at ${estimatedSizeInKb(
    //       args[0]
    //     )}kb`,
    //     ...args
    //   )
    // } else {
    //   if (window.jitsu) window.jitsu(method, ...args)
    // }

    switch (method) {
      case 'id':
        if (Object.keys(args[0]).length === 1 && args[0].id) {
          if (window.jitsuNext) window.jitsuNext.identify(userId)
          else if (window.jitsuNextQ) window.jitsuNextQ.push((jitsu: any) => jitsu.identify(userId))
        } else {
          const latestFlowId = args[0].latest_flow_id
          const entryData = JSON.parse(args[0].all_data)[latestFlowId]
          const prevEntryData = PREV_CLOUD_ENTRY_DATA[latestFlowId]
          const entryDataDiff = prevEntryData ? getDiff(entryData, prevEntryData) : null

          if (prevEntryData && !entryDataDiff?.length) return Promise.resolve()

          const eventData = {
            ...ongoingTrackData,
            ...ongoingTrackDataForFlow[latestFlowId],
            flow_id: args[0].latest_flow_id,
            entry_id: entryData.entryId,
            entry_data: JSON.stringify(entryData),
            entry_data_diff: JSON.stringify(entryDataDiff),
          }

          if (tooLarge(eventData, 25)) {
            console.warn(
              `Cannot track User Data in Embeddables Analytics - Data for Embeddable '${latestFlowId}' is too large at ${estimatedSizeInKb(
                args[0]
              )}kb`,
              ...args
            )
          } else {
            if (window.jitsuNext) window.jitsuNext.track('data:updated', eventData)
            else if (window.jitsuNextQ)
              window.jitsuNextQ.push((jitsu: any) => jitsu.track('data:updated', eventData))
          }

          PREV_CLOUD_ENTRY_DATA[latestFlowId] = entryData
        }

        break

      case 'track': {
        const trackData = {
          ...ongoingTrackData,
          ...ongoingTrackDataForFlow[args[1].flow_id],
          ...args[1],
        }
        if (window.jitsuNext) return window.jitsuNext.track(args[0], trackData)
        if (window.jitsuNextQ)
          return new Promise<void>((resolve, reject) => {
            window.jitsuNextQ.push(async (jitsu: any) => {
              try {
                await jitsu.track(args[0], trackData)
                resolve()
              } catch (err) {
                reject(err)
              }
            })
          })
        break
      }

      default:
        break
    }
  } catch (error) {
    console.error(`Error in Jitsu analytics ${method} call:`, error)
  }
  return Promise.resolve()
}
function tooLarge(obj: object, maxSizeInKb: number) {
  return estimatedSizeInKb(obj) > maxSizeInKb
}
function estimatedSizeInKb(obj: object) {
  if (!obj) return 0
  const charsPerKb = 1024
  const length = (JSON.stringify(obj) && JSON.stringify(obj).length) || 0
  return length / charsPerKb
}
function getGlobalUserId(project_id?: string) {
  const localStorageKey = `embeddables-${project_id}-user-id`
  let id = localStorageGet(localStorageKey)
  if (!id) {
    id = generateId('contact')
    localStorageSet(localStorageKey, id)
  }
  return id
}
function getPageloadId() {
  return `pageload_${new Date().toISOString()}-${Math.random().toString(20).slice(3)}`
}
function getLegacyProductId() {
  const el = document.getElementsByTagName('savvy-smart-form')[0]
  if (!el) return
  return el.getAttribute('id')
}

function setUpJitsuNext() {
  const jitsuDebugMode = searchParams.get('jitsu_debug') === 'true'

  var scriptElement = document.createElement('script')
  scriptElement.async = true
  scriptElement.src = 'https://data.embeddables.com/p.js'
  scriptElement.setAttribute('data-onload', 'jitsuLoaded')
  scriptElement.setAttribute('data-init-only', 'true')
  if (jitsuDebugMode) scriptElement.setAttribute('data-debug', 'true')
  scriptElement.dataset.namespace = 'jitsuNext'
  document.body.appendChild(scriptElement)

  window.jitsuNextQ = []

  return new Promise((resolve, reject) => {
    window.jitsuLoaded = function (jitsu: any) {
      resolve(jitsu)
    }
  })
}

function getDiff(obj1: any, obj2: any) {
  // Get array of { key, old_value, new_value } objects
  const diff: { key: string; old_value: any; new_value: any }[] = []
  const allKeys = Object.keys({ ...obj1, ...obj2 })
  allKeys.forEach((key) => {
    if (!isEqual(obj1, key, obj2)) {
      diff.push({
        key,
        old_value: obj2[key],
        new_value: obj1[key],
        // type: 'computed_field' | 'input',
        // component_type: 'PlainText'
      })
    }
  })

  return diff
}

function isEqual(obj1: any, key: string, obj2: any) {
  return typeof obj1 === 'object' || typeof obj2 === 'object'
    ? JSON.stringify(obj1[key]) === JSON.stringify(obj2[key])
    : obj1[key] === obj2[key]
}

function allowJitsu1_0() {
  if (window.location.search.includes('jitsu_1_0=true')) return true
  if (window.location.search.includes('jitsu_1_0=false')) return false

  return false

  // const hostname = window.location.hostname
  // return (
  //   !(hostname === 'brilliant.org' || hostname.endsWith('.brilliant.org')) &&
  //   ![
  //     'lets.getinflow.io',
  //     'try.getinflow.io',
  //     'join.getinflow.io',
  //     'getinflow.io',
  //     'inflow.clinic',
  //     'get.inflow.clinic',
  //     'staging.d3h18sicxdfo8a.amplifyapp.com',
  //     'www.lets.getinflow.io',
  //     'www.try.getinflow.io',
  //     'www.join.getinflow.io',
  //     'www.getinflow.io',
  //     'www.inflow.clinic',
  //     'www.get.inflow.clinic',
  //     'www.staging.d3h18sicxdfo8a.amplifyapp.com',
  //   ].includes(window.location.hostname)
  // )
}

function allowJitsuNext() {
  if (window.location.search.includes('jitsu_next=true')) return true
  if (window.location.search.includes('jitsu_next=false')) return false

  return true
}
